import { Component } from '@angular/core';
import { SurfaceTier } from '@app/core/models/types/surface-tier.type';
import { BigCommerceLinkService } from '@app/core/services/bigcommerce-link.service';
import { BigCommerceModeStateService } from '@app/core/services/bigcommerce-mode-state.service';
import { BootstrapThemeProp } from '@app/core/services/bootstrap-theme.service';
import { DateTimeService } from '@app/core/services/date-time.service';
import { LinkService } from '@app/core/services/link.service';
import { SurfaceUpgradeFeatureProp } from '@app/core/services/surface-upgrade-feature.service';
import { ExportProtocol } from '@app/exports/models/enums/export-protocol.enum';
import { ExportInterface } from '@app/exports/models/interfaces/export.interface';
import { Modal } from '@app/modules/modals/modals.module';
import { CronService } from '@app/modules/schedule/services/cron.service';
import { faArrowUpRightFromSquare, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, take, takeUntil, tap } from 'rxjs';

export interface PushExportModalResolverInterface {
    export: ExportInterface;
    derivedChannelDestination: string;
    skuLimitAlert?: {
        tier: SurfaceTier;
        feature: SurfaceUpgradeFeatureProp;
    };
}

@Component({
    selector: 'fdx-push-export-dialog',
    templateUrl: './push-export-dialog.component.html',
    styleUrl: './push-export-dialog.component.scss',
    standalone: false
})
export class PushExportDialogComponent implements Modal {
    protected productsUrl: string;
    private readonly unsubscribe$: Subject<void> = new Subject<void>();

    public modalTheme: BootstrapThemeProp = 'secondary';
    public resolve: PushExportModalResolverInterface = {
        export: null,
        derivedChannelDestination: null,
        skuLimitAlert: null
    };
    public export: Partial<ExportInterface>;
    public derivedChannelDestination: string;
    externalLinkIcon: IconDefinition = faArrowUpRightFromSquare;
    skuLimitAlert?: {
        tier: SurfaceTier;
        feature: SurfaceUpgradeFeatureProp;
    };
    isWebhookExport: boolean;

    constructor(
        protected readonly bigCommerceLinkService: BigCommerceLinkService,
        protected readonly bigCommerceSelfService: BigCommerceModeStateService,
        private readonly cronService: CronService,
        private readonly dateTimeService: DateTimeService,
        protected readonly linkService: LinkService,
        private readonly modal: NgbActiveModal
    ) { }

    onModalInit(data: PushExportModalResolverInterface): void {
        this.export = data.export;
        this.derivedChannelDestination = data.derivedChannelDestination;
        this.skuLimitAlert = data.skuLimitAlert;
        this.isWebhookExport = this.export.protocol === ExportProtocol.Webhook;

        this.bigCommerceLinkService.getProductsUrlForChannel(this.export)
            .pipe(
                take(1),
                tap((url) => (this.productsUrl = url)),
                takeUntil(this.unsubscribe$)
            ).subscribe();
    }

    public onModalSubmit(_event: MouseEvent): void {
        this.modal.close();
    }

    public onModalCancel(_event: MouseEvent): void {
        this.modal.dismiss();
    }

    get destinationUrl(): string {
        return this.derivedChannelDestination;
    }

    get nextScheduledExport(): string {
        if (!this.export?.cron) {
            return '';
        }
        return this.cronService.getNextTimeFromCron(this.export.cron, this.export.cron_timezone);
    }

    get timeToNextExport(): string {
        if (!this.export?.cron) {
            return '';
        }
        return this.dateTimeService.getFormattedTimeDifference(this.nextScheduledExport);
    }

    get modalTitle(): string {
        if (this.bigCommerceSelfService.showRestrictedUI()) {
            return 'Run manual sync';
        }
        if (this.export?.paused === '1') {
            return 'Export paused';
        }
        return 'Confirm push export';
    }

    get submitText(): string {
        if (this.bigCommerceSelfService.showRestrictedUI()) {
            return 'Run manual sync';
        }

        if (this.export?.paused === '0') {
            return 'Confirm';
        }
        return 'Still export';
    }
}
